/**
 * `src/auth/` barrel — platform-neutral auth utilities.
 *
 * All exports here are safe for web AND React Native. Web-only auth
 * helpers (Turnkey vendor SDK, sessionStorage stash, popup/redirect
 * connect hooks) live in `src/platform/web/auth/` and are exported
 * from `src/platform/web/index.ts`.
 */

// Cross-provider auth-sync bridge (React context, no vendor/DOM deps)
export {
  type AuthSyncContextValue,
  AuthSyncProvider,
  type CompleteOauthParams,
  type ConnectFailureReason,
  type FreshLoginData,
  type OAuthProfile,
  type PendingConnect,
  useAuthSync,
} from './oauth/auth-sync.js';
// OAuth / OIDC claim utilities (pure functions, no DOM/RN deps)
export {
  CONNECTABLE_PROVIDERS,
  decodeJwtPayload,
  detectOAuthProvider,
  extractProfileFromOidc,
  extractProviderUserId,
  findTurnkeyProvider,
  normalizeProviderName,
  type OAuthCapturedResult,
  type ProviderProfile,
  turnkeyAlreadyHoldsIdentity,
  validateGoogleEmailMatch,
} from './oauth/oauth-claims.js';
// OAuth return error (X query / Apple+Google fragment), pure function
export { readOauthReturnError } from './oauth/oauth-return-error.js';
// Platform-neutral waitlist X join helpers (signup v2, pure functions)
export {
  buildXWaitlistStartUrl,
  readXWaitlistFromUrl,
  type XWaitlistJoinResult,
} from './oauth/x-waitlist-result.js';
// SDK auth store (platform-neutral)
export {
  getSdkAuthStore,
  type MorfiUser,
  type SdkAuthState,
  type SdkAuthStatus,
  type SdkAuthStore,
  type SdkConnectedProvider,
} from './state/auth-state.js';
// SDK transport-credential types (internal — useSdkCredentialKind is NOT
// re-exported from the public barrel; internal/devtool code imports it from
// './state/use-sdk-credential-kind.js' directly)
export type { SdkCredential, SdkCredentialKind } from './state/use-sdk-credential-kind.js';
// Turnkey modal-active bridge (React context, no vendor/DOM deps)
export {
  TurnkeyModalActiveProvider,
  useTurnkeyModalActive,
} from './turnkey/turnkey-modal-context.js';

// ── Relocated from frontend (spec 009 FILE 14 Group B/C) ─────────────────────

// Canonical public identity materializer shared by auth, profiles, and embedded rows.
export { usePublicUserIdentity } from '../core/hooks/usePublicUserIdentity.js';
// Impersonation overlay context — injects isActive/target from ImpersonationProvider
export {
  type ImpersonatedProvider,
  type ImpersonationOverlay,
  ImpersonationOverlayProvider,
  type ImpersonationTarget,
  useImpersonationOverlay,
} from './impersonation/auth-overlay-context.js';
// ImpersonationProvider — platform-neutral React component
export { ImpersonationProvider } from './impersonation/context.js';
// Impersonation types — platform-neutral (spec 009 FILE 14 Group F)
export type {
  ConnectedProviderWire,
  GrantApiErrorCode,
  GrantApiErrorPayload,
  GrantConfig,
  GrantStatusResponse,
  ImpersonationActivatePayload,
  ImpersonationConnectedProvider,
  ImpersonationState,
  MintGrantResponse,
  ResolveTargetResponse,
  TeardownReason,
} from './impersonation/types.js';
// Auth actions context — injects logout/refreshSession/setUser from AuthProvider
export {
  type AuthActions,
  AuthActionsProvider,
  useAuthActions,
} from './state/auth-actions-context.js';
// clearUserStorage — user-scoped storage cleanup on logout
export { clearUserStorage } from './state/cleanup.js';
// useAuth — primary auth hook with impersonation overlay
export { type AuthValue, useAuth } from './state/use-auth.js';
// useEngineerIdentity — raw auth hook for telemetry (no overlay)
export { useEngineerIdentity } from './state/use-engineer-identity.js';
// Active wallet context — injects activeWalletId/setActiveWalletId from Zustand store
export {
  ActiveWalletProvider,
  type ActiveWalletSlot,
  useActiveWalletSlot,
} from './wallets/active-wallet-context.js';
// useActiveWallet — active wallet hook with impersonation overlay
export { type UseActiveWalletReturn, useActiveWallet } from './wallets/use-active-wallet.js';
// useWallets + useArchivedWallets — wallet hooks with impersonation overlay
export { useArchivedWallets, useWallets } from './wallets/use-wallets.js';
// Wallets context + providers — inject wallet data from TanStack Query + Turnkey
export {
  ArchivedWalletsProvider,
  type SdkCreateWalletParams,
  type SdkExportWalletParams,
  type SdkExportWalletResult,
  type SdkImportWalletParams,
  type SdkRenameWalletParams,
  type SdkWallet,
  type SdkWalletType,
  type UseArchivedWalletsResult,
  type UseWalletsResult,
  WalletsProvider,
} from './wallets/wallets-context.js';

// ── Relocated from frontend (spec 009 FILE 14 Group E — tx signing boundary) ──

// Turnkey submission protocol types — all Turnkey HTTP API interface declarations
// plus the TurnkeyDeps capability bundle and its inert fallback factory.
// Consumed by the frontend tx-engine and the web platform submission hook.
export {
  createInertTurnkeyDeps,
  type SolSendTransactionInput,
  type TGetActivityBody,
  type TGetActivityResponse,
  type TGetGasUsageBody,
  type TGetGasUsageResponse,
  type TGetSendTransactionStatusBody,
  type TGetSendTransactionStatusResponse,
  type TurnkeyDeps,
  type V1Activity,
  type V1SolanaFailureDetails,
  type V1SolanaSendTransactionStatus,
  type V1TxError,
} from '../tx-engine/turnkey-types.js';
// Signing type definitions — pure TypeScript interfaces for the tx submission
// pipeline. Runtime functions (signTransaction, signMessage, resolveSigningMethod)
// remain in the frontend tx-engine pending impersonation-store dependency migration.
export type {
  ExternalWalletProvider,
  SigningDeps,
  TurnkeySignMessageFn,
  TurnkeySignTransactionFn,
} from './turnkey/signing-types.js';
